import { useEffect, useRef, useState } from "react";
import Modal from "../../base/Modal";
import { readFile } from "../../../helpers/cropImage";
import CoverPhotoCropModalContent from "../../common/SavedPlaces/CoverPhotoCropModalContent";
import { useImageCropContext } from "../../../providers/ImageCropProvider";
import { useI18N, usePostCoverPhotoDesktop } from "common/hooks";

import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import Loader from "../Loader";
import { useCustomToast } from "@/hooks/useCustomToast";

interface SavedPlace {
  cover_photo: string;
  created_at: string;
  id: number;
  name: string;
  saved_place_details: object;
  total_places: number;
  updated_at: string;
  user_id: number;
  // Add any other fields that are part of your saved place object
}

type CoverPhotoCropProps = {
  list: SavedPlace;
  initiateOpenModal: boolean;
  onClose: () => void;
};

const CoverPhotoCrop = ({
  list,
  initiateOpenModal,
  onClose,
}: CoverPhotoCropProps) => {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const fileInputRef = useRef<HTMLInputElement>(null); // Add ref for file input

  const [openModal, setOpenModal] = useState(false);
  const [, setPreview] = useState("");

  const { updateCoverPhoto, error, isLoading, result } =
    usePostCoverPhotoDesktop();

  const { getProcessedImage, setImage, resetStates } = useImageCropContext();

  const showToast = useCustomToast();

  const handleDone = async () => {
    const avatarBlob = await getProcessedImage();
    console.log("avatar", avatarBlob.url);

    const imageFile = new File([avatarBlob.file], `img-${Date.now()}.png`, {
      type: "image/png",
    });
    setPreview(window.URL.createObjectURL(imageFile));

    updateCoverPhoto({
      coverPhoto: avatarBlob.file || "",
      savedPlaceListId: list?.id.toString(),
    });

    AnalyticsService.trackGAEventHandler("updateCoverPhoto2CoverPhotoUpdated");
    AnalyticsService.trackMx("updateCoverPhoto2CoverPhotoUpdated", {
      SavedPlaceName: list.name,
      SavedPlaceId: list.id,
      SavedPlaceTotalPlaces: list.total_places,
    });

    resetStates();
    setOpenModal(false);
  };

  const handleFileChange = async ({ target: { files } }) => {
    console.log("handleFileChange");
    const file = files && files[0];
    const imageDataUrl = await readFile(file);
    setImage(imageDataUrl);
    setOpenModal(true);

    // Reset the file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("updateCoverPhoto2ErrorUploadingCoverPhoto");
      AnalyticsService.trackMx("updateCoverPhoto2ErrorUploadingCoverPhoto", {
        SavedPlaceName: list.name,
        SavedPlaceId: list.id,
        SavedPlaceTotalPlaces: list.total_places,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark, list.id, list.name, list.total_places]);

  useEffect(() => {
    if (result) {
      showToast(i18n.get("listCoverPhotoUpdated"), "success");
    }
  }, [result, isDark, i18n]);

  useEffect(() => {
    // Auto-trigger file input click when modal is opened
    if (initiateOpenModal) {
      fileInputRef.current.click();
    }
  }, [initiateOpenModal]);

  const handleModalClose = () => {
    AnalyticsService.trackGAEventHandler("updateCoverPhoto2ExitedUpdatePhoto");
    AnalyticsService.trackMx("updateCoverPhoto2ExitedUpdatePhoto", {});

    setOpenModal(false);
    onClose();
  };

  return (
    <div aria-label="Saved Place cover photo" className="relative">
      {isLoading && <Loader />}
      <input
        type="file"
        onChange={handleFileChange}
        className="hidden"
        id="avatarInput"
        accept="image/*"
        ref={fileInputRef} // Attach ref to file input
      />

      <Modal open={openModal} handleClose={() => handleModalClose()}>
        <CoverPhotoCropModalContent
          handleDone={handleDone}
          handleClose={() => handleModalClose()}
        />
      </Modal>
    </div>
  );
};

export default CoverPhotoCrop;
